import {useRouter} from 'next/router';
import {ReactNode, useCallback, useMemo} from 'react';
import {FetchStatus} from '../../../common/FetchStatus.enum';
import {requestContext} from '../../../common/requestContext';
import {getDataset} from '../../../common/shared/utils/getDataset';
import {ReactQueryInfiniteType} from '../../../reactQuery/CodegenReactQueryType.type';
import {ReactQueryInfiniteFetcher} from '../../../reactQuery/ReactQueryInfiniteFetcher';
import {useLazyReactQuery} from '../../../reactQuery/useLazyReactQuery';
import {ASK_NUM_PER_PAGE, ASK_PROJECT_ID} from '../constant';
import useAskDetailQueryHook from '../hook/useAskDetailQueryHook';
import useAskListQueryHook from '../hook/useAskListQueryHook';
import useInfiniteAskListQueryHook from '../hook/useInfiniteAskListQueryHook';
import {AskMyProps} from '../presentational/AskMy';
import {AskData} from '../type';

interface AskMyContainerProps {
  children: (props: AskMyProps) => ReactNode;
}

export function AskMyContainer({children}: AskMyContainerProps) {
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const isGlobal = requestContext.requestInfo.isGlobal;
  const router = useRouter();
  const currentPage = Number(router?.query?.page) || 1;
  const projectId = isGlobal ? ASK_PROJECT_ID.PageGlobal : ASK_PROJECT_ID.Page;

  const {
    data: listData,
    isSuccess,
    isError,
  } = useAskListQueryHook({
    variables: {
      current: currentPage,
      rowsPerPage: ASK_NUM_PER_PAGE,
      projectId,
    },
  });

  const {fetchWithVariables: fetchAskDetail} = useLazyReactQuery(
    useAskDetailQueryHook,
    {options: {enabled: false}}
  );

  const onChangePage = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const newPage = Number(getDataset(e.currentTarget, 'page'));
      if (currentPage === newPage) {
        return;
      }
      router.push(`/ask?tab=my&page=${newPage}`, undefined, {scroll: false});
    },
    [currentPage, router.push]
  );

  const openAskDetail = useCallback(
    async (askId: number) => {
      const result = await fetchAskDetail({questionId: askId, projectId});
      return result.data;
    },
    [fetchAskDetail, projectId]
  );

  const fetchStatus = useMemo(() => {
    if (isSuccess) {
      return FetchStatus.Success;
    }
    if (isError) {
      return FetchStatus.Error;
    }
    return FetchStatus.Loading;
  }, [isSuccess, isError]);

  return (
    <>
      {isMobile && (
        <ReactQueryInfiniteFetcher
          reactQuery={useInfiniteAskListQueryHook}
          type={ReactQueryInfiniteType.InfinityScroll}
          variables={{
            // 모웹의 경우 인피니티 스크롤이므로 query의 page 파라미터를 사용하지 않습니다.
            current: 1,
            rowsPerPage: 10,
            projectId,
          }}
          /**
           * ReactQueryInfiniteFetcher 의 children data 타입 추론을 위해 options 에 아래 항목이 필수로 들어가야합니다.
           * 실제로 사용은 되지 않습니다.
           *  */
          options={{
            initialPageParam: {
              current: 1,
              rowsPerPage: 10,
            },
            getNextPageParam: () => undefined,
          }}
        >
          {data => {
            return children({
              isMobile,
              isWebView,
              currentPage,
              totalCount: data?.pages[0]?.page.totalResult ?? 0,
              fetchedAskList: {
                data:
                  data?.pages.reduce(
                    (questions, page) =>
                      questions.concat(page?.questions || []),
                    [] as AskData[]
                  ) ?? [],
                fetchStatus,
              },
              onChangePage,
              openAskDetail,
            });
          }}
        </ReactQueryInfiniteFetcher>
      )}
      {!isMobile && (
        <>
          {children({
            isMobile,
            isWebView,
            currentPage,
            totalCount: listData?.page?.totalResult ?? 0,
            fetchedAskList: {
              data: listData?.questions ?? [],
              fetchStatus,
            },
            onChangePage,
            openAskDetail,
          })}
        </>
      )}
    </>
  );
}
